import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Grid, Divider } from '@mui/material';
import { type OtherTestFormProps } from './types';
import { APPLICATION_TEXT, PAGE_COMMON_TEXT } from '../../lang';
import { PAGE_TEXT } from '../../../../Application/components/ApplicationForm/lang';
import { LabelValue, DocumentBox } from '../../../../../components';
import { type RenderData } from '../../types';
import { getOptionsLabel } from '../../getOptionsLabel';
import { otherTestTypeOptions } from '../../../../Application/components/ApplicationForm/OtherTest';
import { OTHER_TEST_TYPE } from '../../../../Application/components';
import { checkInvalidDate } from '../../../../Application/graphql';

const OTHER_TEST = APPLICATION_TEXT.OTHER_TEST;

export const OtherTestForm: React.FunctionComponent<OtherTestFormProps> = (
  props: OtherTestFormProps,
) => {
  const { data } = props;
  const {
    testType,
    testDate,
    resultAnnouncedIndicator,
    expectedResultUpdateDate,
    result,
    referenceNo,
    supportingDocuments = [],
  } = data ?? {};
  const {
    resultReceiptDate,
    totalScore,
    totalPercentile,
    quantitativeScore,
    quantitativePercentile,
    analyticalWritingScore,
    analyticalWritingPercentile,
    verbalScore,
    verbalPercentile,
    integratedReasoningScore,
    integratedReasoningPercentile,
    dataInsightsScore,
    dataInsightsPercentile,
  } = result ?? {};

  const testTypeLabel = getOptionsLabel(
    otherTestTypeOptions,
    testType as string,
  );

  const renderData: RenderData[] = [
    {
      label: OTHER_TEST.TEST_TYPE,
      value: testTypeLabel,
      isFullWidth: true,
      display: true,
    },

    ...(resultAnnouncedIndicator
      ? [
          {
            label: OTHER_TEST.TEST_DATE,
            value: checkInvalidDate(testDate)
              ? APPLICATION_TEXT.NA
              : dayjs(testDate).format('DD/MM/YYYY'),
            display: true,
          },
          {
            label: OTHER_TEST.DATE_OF_RESULT,
            value: checkInvalidDate(resultReceiptDate)
              ? APPLICATION_TEXT.NA
              : dayjs(resultReceiptDate).format('DD/MM/YYYY'),
            display: true,
          },
          {
            label: OTHER_TEST.TOTAL_SCORE,
            value: totalScore,
            display: testType !== OTHER_TEST_TYPE.GRE,
          },
          {
            label: OTHER_TEST.PERCENTILE,
            value: totalPercentile + '%',
            display: testType !== OTHER_TEST_TYPE.GRE,
          },
          {
            label: OTHER_TEST.VERBAL,
            value: verbalScore,
            display: true,
          },
          {
            label: OTHER_TEST.PERCENTILE,
            value: verbalPercentile + '%',
            display: true,
          },
          {
            label: OTHER_TEST.QUANTITATIVE,
            value: quantitativeScore,
            display: true,
          },
          {
            label: OTHER_TEST.PERCENTILE,
            value: quantitativePercentile + '%',
            display: true,
          },
          {
            label: OTHER_TEST.ANALYTICAL_WRITING,
            value: analyticalWritingScore,
            display:
              testType === OTHER_TEST_TYPE.GRE ||
              testType === OTHER_TEST_TYPE.GMAT,
          },
          {
            label: OTHER_TEST.PERCENTILE,
            value: analyticalWritingPercentile + '%',
            display:
              testType === OTHER_TEST_TYPE.GRE ||
              testType === OTHER_TEST_TYPE.GMAT,
          },

          {
            label: OTHER_TEST.INTEGRATED_REASONING,
            value: integratedReasoningScore,
            display:
              testType === OTHER_TEST_TYPE.GMAT_ONLINE ||
              testType === OTHER_TEST_TYPE.GMAT,
          },
          {
            label: OTHER_TEST.PERCENTILE,
            value: integratedReasoningPercentile + '%',
            display:
              testType === OTHER_TEST_TYPE.GMAT_ONLINE ||
              testType === OTHER_TEST_TYPE.GMAT,
          },
          {
            label: OTHER_TEST.DATA_INSIGHT,
            value: dataInsightsScore,
            display: testType === OTHER_TEST_TYPE.GMAT_FOCUS,
          },
          {
            label: OTHER_TEST.PERCENTILE,
            value: dataInsightsPercentile + '%',
            display: testType === OTHER_TEST_TYPE.GMAT_FOCUS,
          },
          {
            label: PAGE_TEXT.QUALIFICATION.OTHER_TEST.REFERENCE_NO,
            value: referenceNo ?? APPLICATION_TEXT.NA,
            isFullWidth: true,
            display: true,
          },
        ]
      : [
          {
            label: OTHER_TEST.EXPECTED_UPDATED_DATE,
            value: dayjs(expectedResultUpdateDate).format('DD/MM/YYYY'),
            display: true,
          },
        ]),
  ];

  const hasFiles = supportingDocuments.length > 0;
  const filteredRenderData = renderData.filter(data => data.display);

  return data !== undefined ? (
    <Container>
      <Grid container spacing={2} padding={1}>
        {filteredRenderData.map((item, index) => {
          const isLastValue =
            index === filteredRenderData.length - 1 &&
            filteredRenderData.length % 2 !== 0;
          return (
            <Grid key={index} size={isLastValue || item.isFullWidth ? 12 : 6}>
              <LabelValue
                label={item.label}
                value={item.value as string | number}
              />
              <Divider />
            </Grid>
          );
        })}
      </Grid>
      <div className="supporting-documents">
        <h3>{APPLICATION_TEXT.SUPPORTING_DOCUMENT}</h3>
        <div className="document-display">
          {hasFiles ? (
            supportingDocuments.map((file, index) => {
              return <DocumentBox key={index} file={file} />;
            })
          ) : (
            <div>
              <p>{APPLICATION_TEXT.NA}</p>
            </div>
          )}
        </div>
      </div>
    </Container>
  ) : (
    <p>{PAGE_COMMON_TEXT.NA}</p>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .supporting-documents {
    padding: 0.5rem 0 0 2rem;

    .document-display {
      padding: 0.5rem 1rem;
    }

    h3 {
      font-style: italic;
      color: #8c8c8c;
    }
  }

  .MuiBox-root {
    padding: 0 0 0.1rem 0.5rem;
  }

  .MuiGrid-item {
    padding-left: 0 !important;
  }

  .MuiGrid-container {
    padding: 0.5rem 1rem 0 2rem !important;
  }
`;
